import { Component, OnInit, Input, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import { DrupalRESTService } from '../../../services/drupal-rest.service';
import { AuthService } from '../../../services/auth.service';
import { DialogService } from '../../../services/dialog.service';
import { ReportDateService } from '../../../services/report-date.service';
import { PermissionsService } from '../../../services/permissions.service';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { ActivityHistoryDialogComponent } from '../activity-history-dialog/activity-history-dialog.component';
import { LoggerService } from '../../../services/logger.service';

export interface Activity {
  type: 'enrollment' | 'payment' | 'lesson' | 'student' | 'system';
  title: string;
  description: string;
  timestamp: string;
  time_ago: string;
  entity_id: string;
  studio_reference: number;
}

export interface ActivityResponse {
  success: boolean;
  data: Activity[];
  count: number;
  studio_reference: number;
  has_more?: boolean;
  page?: number;
  total_pages?: number;
}

@Component({
  selector: 'app-activity-feed',
  templateUrl: './activity-feed.component.html',
  styleUrls: ['./activity-feed.component.css']
})
export class ActivityFeedComponent implements OnInit, OnDestroy {
  @Input() maxItems: number = 10;
  @ViewChild('scrollContainer') scrollContainer: ElementRef;
  
  activities: Activity[] = [];
  displayedActivities: Activity[] = [];
  loading: boolean = true;
  error: string = '';
  
  private studioChangeSubscription: Subscription;
  private refreshInterval: any;

  constructor(
    private drupalRESTService: DrupalRESTService,
    private authService: AuthService,
    private dialogService: DialogService,
    private reportDateService: ReportDateService,
    private permissionsService: PermissionsService
  ) { }

  ngOnInit(): void {
    this.loadRecentActivity();
    this.subscribeToStudioChanges();
    
    // Refresh activity feed every 60 seconds (only refreshes first page)
    this.refreshInterval = setInterval(() => {
      this.loadRecentActivity(true);
    }, 60000);
  }

  ngOnDestroy(): void {
    if (this.studioChangeSubscription) {
      this.studioChangeSubscription.unsubscribe();
    }
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }

  private subscribeToStudioChanges(): void {
    // skip(1) drops the BehaviorSubject's replay of the current value. ngOnInit already loaded
    // the initial studio's data directly, so only act on *subsequent* studio changes.
    this.studioChangeSubscription = this.authService.selectedStudioChanged.pipe(skip(1)).subscribe((studio) => {
      if (studio && studio.id) {
        LoggerService.log('Activity Feed: Studio changed, reloading activity');
        this.activities = [];
        this.loadRecentActivity();
      }
    });
  }

  private loadRecentActivity(isRefresh: boolean = false): void {
    const studioId = this.authService.selectedStudio?.id;
    
    if (!studioId) {
      LoggerService.log('Activity Feed: No studio selected');
      this.loading = false;
      this.activities = [];
      this.updateDisplayedActivities();
      return;
    }

    this.loading = !isRefresh || this.activities.length === 0;
    this.error = '';
    
    // Always load just the first page for dashboard feed (no endless scrolling here)
    const endpoint = `/amt_dashboard/recent-activity/${studioId}?page=0&limit=${this.maxItems}`;
    
    this.drupalRESTService.httpGET(endpoint).subscribe({
      next: (response: any) => {
        if (response && response.success && response.data) {
          this.activities = response.data;
          this.updateDisplayedActivities();
        } else {
          this.activities = [];
          this.updateDisplayedActivities();
        }
        this.loading = false;
      },
      error: (error) => {
        LoggerService.error('Error loading recent activity:', error);
        if (!isRefresh) {
          this.error = 'Failed to load recent activity';
          this.activities = [];
          this.updateDisplayedActivities();
        }
        this.loading = false;
      }
    });
  }

  private updateDisplayedActivities(): void {
    // Show all activities (already limited by the API)
    this.displayedActivities = this.activities.filter(activity => this.canViewActivityType(activity.type));
  }

  refreshActivity(): void {
    this.activities = [];
    this.loadRecentActivity();
  }

  getIcon(type: string): string {
    const iconMap = {
      'enrollment': 'school',
      'payment': 'payment',
      'lesson': 'event',
      'student': 'person_add',
      'system': 'info'
    };
    return iconMap[type] || 'info';
  }

  getIconColor(type: string): string {
    const colorMap = {
      'enrollment': 'primary',
      'payment': 'success',
      'lesson': 'accent',
      'student': 'info',
      'system': 'warning'
    };
    return colorMap[type] || 'default';
  }

  getIconColorClass(type: string): string {
    const color = this.getIconColor(type);
    return `icon-${color}`;
  }

  getTimeAgo(activity: Activity): string {
    // Use the pre-calculated time_ago from the server
    return activity.time_ago || this.calculateTimeAgo(activity.timestamp);
  }

  private calculateTimeAgo(timestamp: string): string {
    const now = Date.now();
    const activityTime = parseInt(timestamp) * 1000; // Convert Unix timestamp to milliseconds
    const diff = now - activityTime;
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
    if (weeks < 4) return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    
    return this.reportDateService.formatDate(activityTime);
  }

  viewAllActivity(): void {
    if (!this.hasVisibleActivityTypes()) {
      return;
    }
    this.dialogService.openDialog(ActivityHistoryDialogComponent, 'largerScreenDashboard', {
      studioId: this.authService.selectedStudio?.id
    })
    .afterClosed()
    .subscribe(result => {
      // Optionally refresh the feed after closing the dialog
      if (result === 'refresh') {
        this.refreshActivity();
      }
    });
  }

  private canViewActivityType(type: Activity['type']): boolean {
    switch (type) {
      case 'student':
        return this.permissionsService.canAccess('students_view');
      case 'enrollment':
        return this.permissionsService.canAccess('enrollments_view');
      case 'payment':
        return this.permissionsService.canAccess('payments_view');
      case 'lesson':
        return this.permissionsService.canAccess('calendar_view');
      case 'system':
      default:
        return true;
    }
  }

  private hasVisibleActivityTypes(): boolean {
    return this.permissionsService.canAccess('students_view')
      || this.permissionsService.canAccess('enrollments_view')
      || this.permissionsService.canAccess('payments_view')
      || this.permissionsService.canAccess('calendar_view');
  }
}
